/**
 * Role-Based Access Control (RBAC) Permissions Hook
 * 
 * Roles (Low → High Privilege):
 * 1. medic - Worker Portal only
 * 2. ops_coordinator - Operations (replaces 'standard')
 * 3. manager - Supervisory + Compliance authority (replaces 'team_leader')
 * 4. admin - Full system access
 */

import { useAuth } from '../context/AuthContext';

// Permission constants (must match backend)
export const Permissions = {
  // Document permissions
  UPLOAD_OWN_DOCS: 'upload_own_docs',
  UPLOAD_DOCS_FOR_OTHERS: 'upload_docs_for_others',
  APPROVE_DOCUMENTS: 'approve_documents',
  REJECT_DOCUMENTS: 'reject_documents',
  VIEW_ALL_DOCUMENT_HISTORY: 'view_all_document_history',
  
  // Export permissions
  EXPORT_CSV: 'export_csv',
  
  // Audit permissions
  ACCESS_AUDIT_LOGS: 'access_audit_logs',
  
  // Operations permissions
  SEND_REMINDERS: 'send_reminders',
  CREATE_SHIFTS: 'create_shifts',
  BOOK_SHIFTS: 'book_shifts',
  APPROVE_EXPENSES: 'approve_expenses',
  
  // Admin permissions
  ACCESS_SETTINGS: 'access_settings',
  MANAGE_USERS: 'manage_users',
  MANAGE_ROLES: 'manage_roles',
  CONFIGURE_NOTIFICATIONS: 'configure_notifications',
  
  // View permissions
  VIEW_COMPLIANCE_DASHBOARD: 'view_compliance_dashboard',
  VIEW_REPORTS: 'view_reports',
  VIEW_MEDICS: 'view_medics',
  VIEW_SHIFTS: 'view_shifts',
  VIEW_EXPENSES: 'view_expenses',
};

// Role hierarchy (higher = more privilege)
export const ROLE_HIERARCHY = {
  medic: 0,
  ops_coordinator: 1,
  standard: 1, // Backwards compatibility
  team_leader: 2, // Backwards compatibility - same as manager
  manager: 2,
  admin: 3
};

// Role display names
export const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  team_leader: 'Manager', // Backwards compatibility display
  ops_coordinator: 'Ops Coordinator',
  standard: 'Ops Coordinator', // Backwards compatibility display
  medic: 'Medic',
};

// Role badge styles
export const ROLE_STYLES = {
  admin: 'bg-red-500/15 text-red-400 border-red-500/20',
  manager: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  team_leader: 'bg-blue-500/15 text-blue-400 border-blue-500/20', // Backwards compatibility
  ops_coordinator: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  standard: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20', // Backwards compatibility
  medic: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
};

// Role-Permission mapping
const ROLE_PERMISSIONS = {
  medic: new Set([
    Permissions.UPLOAD_OWN_DOCS,
  ]),
  ops_coordinator: new Set([
    Permissions.UPLOAD_OWN_DOCS,
    Permissions.UPLOAD_DOCS_FOR_OTHERS,
    Permissions.VIEW_ALL_DOCUMENT_HISTORY,
    Permissions.SEND_REMINDERS,
    Permissions.CREATE_SHIFTS,
    Permissions.BOOK_SHIFTS,
    Permissions.APPROVE_EXPENSES,
    Permissions.VIEW_COMPLIANCE_DASHBOARD,
    Permissions.VIEW_MEDICS,
    Permissions.VIEW_SHIFTS,
    Permissions.VIEW_EXPENSES,
  ]),
  standard: new Set([ // Backwards compatibility - same as ops_coordinator
    Permissions.UPLOAD_OWN_DOCS,
    Permissions.UPLOAD_DOCS_FOR_OTHERS,
    Permissions.VIEW_ALL_DOCUMENT_HISTORY,
    Permissions.SEND_REMINDERS,
    Permissions.CREATE_SHIFTS,
    Permissions.BOOK_SHIFTS,
    Permissions.APPROVE_EXPENSES,
    Permissions.VIEW_COMPLIANCE_DASHBOARD,
    Permissions.VIEW_MEDICS,
    Permissions.VIEW_SHIFTS,
    Permissions.VIEW_EXPENSES,
  ]),
  team_leader: new Set([ // Backwards compatibility - same as manager
    Permissions.UPLOAD_OWN_DOCS,
    Permissions.UPLOAD_DOCS_FOR_OTHERS,
    Permissions.VIEW_ALL_DOCUMENT_HISTORY,
    Permissions.SEND_REMINDERS,
    Permissions.CREATE_SHIFTS,
    Permissions.BOOK_SHIFTS,
    Permissions.APPROVE_EXPENSES,
    Permissions.VIEW_COMPLIANCE_DASHBOARD,
    Permissions.VIEW_MEDICS,
    Permissions.VIEW_SHIFTS,
    Permissions.VIEW_EXPENSES,
    Permissions.APPROVE_DOCUMENTS,
    Permissions.REJECT_DOCUMENTS,
    Permissions.EXPORT_CSV,
    Permissions.ACCESS_AUDIT_LOGS,
    Permissions.VIEW_REPORTS,
  ]),
  manager: new Set([
    // Everything ops_coordinator has
    Permissions.UPLOAD_OWN_DOCS,
    Permissions.UPLOAD_DOCS_FOR_OTHERS,
    Permissions.VIEW_ALL_DOCUMENT_HISTORY,
    Permissions.SEND_REMINDERS,
    Permissions.CREATE_SHIFTS,
    Permissions.BOOK_SHIFTS,
    Permissions.APPROVE_EXPENSES,
    Permissions.VIEW_COMPLIANCE_DASHBOARD,
    Permissions.VIEW_MEDICS,
    Permissions.VIEW_SHIFTS,
    Permissions.VIEW_EXPENSES,
    // Plus compliance authority
    Permissions.APPROVE_DOCUMENTS,
    Permissions.REJECT_DOCUMENTS,
    Permissions.EXPORT_CSV,
    Permissions.ACCESS_AUDIT_LOGS,
    Permissions.VIEW_REPORTS,
  ]),
  admin: new Set([
    // Everything manager has + admin-only
    Permissions.UPLOAD_OWN_DOCS,
    Permissions.UPLOAD_DOCS_FOR_OTHERS,
    Permissions.VIEW_ALL_DOCUMENT_HISTORY,
    Permissions.SEND_REMINDERS,
    Permissions.CREATE_SHIFTS,
    Permissions.BOOK_SHIFTS,
    Permissions.APPROVE_EXPENSES,
    Permissions.VIEW_COMPLIANCE_DASHBOARD,
    Permissions.VIEW_MEDICS,
    Permissions.VIEW_SHIFTS,
    Permissions.VIEW_EXPENSES,
    Permissions.APPROVE_DOCUMENTS,
    Permissions.REJECT_DOCUMENTS,
    Permissions.EXPORT_CSV,
    Permissions.ACCESS_AUDIT_LOGS,
    Permissions.VIEW_REPORTS,
    // Admin-only
    Permissions.ACCESS_SETTINGS,
    Permissions.MANAGE_USERS,
    Permissions.MANAGE_ROLES,
    Permissions.CONFIGURE_NOTIFICATIONS,
  ]),
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role, permission) {
  if (!role || !ROLE_PERMISSIONS[role.toLowerCase()]) {
    return false;
  }
  return ROLE_PERMISSIONS[role.toLowerCase()].has(permission);
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role, permissions) {
  return permissions.some(p => hasPermission(role, p));
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role, permissions) {
  return permissions.every(p => hasPermission(role, p));
}

/**
 * Get the hierarchy level of a role
 */
export function getRoleLevel(role) {
  return ROLE_HIERARCHY[role?.toLowerCase()] ?? -1;
}

/**
 * Check if user's role is at or above the minimum required role
 */
export function isRoleAtLeast(userRole, minimumRole) {
  return getRoleLevel(userRole) >= getRoleLevel(minimumRole);
}

/**
 * Hook for permission-based access control
 * Uses effective role when admin is impersonating or when session role is selected
 */
export function usePermissions() {
  const { user, isImpersonating, effectiveRole, actualRole, sessionRole } = useAuth();
  
  // Normalize role (handle team_leader -> manager backwards compatibility)
  const normalizeRole = (r) => {
    if (!r) return '';
    const lower = r.toLowerCase();
    return lower === 'team_leader' ? 'manager' : lower;
  };
  
  // Use effective role if impersonating, session role if selected, otherwise use actual role
  const role = isImpersonating && effectiveRole
    ? normalizeRole(effectiveRole)
    : sessionRole
      ? normalizeRole(sessionRole)
      : normalizeRole(user?.role);
  
  // Always check actual role for admin status (needed for impersonation UI)
  const isActualAdmin = actualRole === 'admin' || user?.role === 'admin';

  return {
    // Role checks (based on effective role)
    role,
    isAdmin: role === 'admin',
    isManager: role === 'manager' || role === 'team_leader',
    isTeamLeader: role === 'manager' || role === 'team_leader', // Backwards compatibility
    isOpsCoordinator: role === 'ops_coordinator' || role === 'standard',
    isMedic: role === 'medic',
    
    // Actual admin check (for impersonation controls)
    isActualAdmin,
    isImpersonating,
    effectiveRole,
    actualRole,
    sessionRole,
    
    // Hierarchy checks
    isAtLeast: (minimumRole) => isRoleAtLeast(role, minimumRole),
    
    // Permission checks
    hasPermission: (permission) => hasPermission(role, permission),
    hasAnyPermission: (permissions) => hasAnyPermission(role, permissions),
    hasAllPermissions: (permissions) => hasAllPermissions(role, permissions),
    
    // Convenience permission checks
    canApproveDocuments: hasPermission(role, Permissions.APPROVE_DOCUMENTS),
    canRejectDocuments: hasPermission(role, Permissions.REJECT_DOCUMENTS),
    canExportCSV: hasPermission(role, Permissions.EXPORT_CSV),
    canAccessAuditLogs: hasPermission(role, Permissions.ACCESS_AUDIT_LOGS),
    canAccessSettings: hasPermission(role, Permissions.ACCESS_SETTINGS),
    canManageUsers: hasPermission(role, Permissions.MANAGE_USERS),
    canSendReminders: hasPermission(role, Permissions.SEND_REMINDERS),
    canCreateShifts: hasPermission(role, Permissions.CREATE_SHIFTS),
    canBookShifts: hasPermission(role, Permissions.BOOK_SHIFTS),
    canApproveExpenses: hasPermission(role, Permissions.APPROVE_EXPENSES),
    canUploadDocsForOthers: hasPermission(role, Permissions.UPLOAD_DOCS_FOR_OTHERS),
    canViewCompliance: hasPermission(role, Permissions.VIEW_COMPLIANCE_DASHBOARD),
    canViewReports: hasPermission(role, Permissions.VIEW_REPORTS),
  };
}

export default usePermissions;
